import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import { cn } from '@/lib/utils';
import type { SearchResult } from './types';

interface PdfResultsSidebarProps {
  results: SearchResult[];
  currentResultIndex: number;
  onSelectResult: (index: number) => void;
  onPrevResult: () => void;
  onNextResult: () => void;
  onClose: () => void;
}

/**
 * A plain panel rather than a Sheet: it is not modal, so it must not trap focus
 * or lock the page scroll while the reader keeps working in the document.
 */
const PdfResultsSidebar: React.FC<PdfResultsSidebarProps> = ({
  results,
  currentResultIndex,
  onSelectResult,
  onPrevResult,
  onNextResult,
  onClose,
}) => {
  const { t } = useTranslation();

  return (
    <aside className="absolute inset-y-0 right-0 z-10 flex w-[280px] flex-col border-l bg-card text-card-foreground shadow-lg">
      <div className="flex items-center justify-between p-4">
        <p className="revy-cjk text-base font-bold leading-[1.5]">
          {t('search.searchResults')} ({results.length})
        </p>
        <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label={t('common.close')}>
          <X />
        </Button>
      </div>
      <Separator />
      <div className="flex items-center justify-center gap-2 p-2">
        <TooltipIconButton
          size="icon-sm"
          label={t('documents.previousResultShortcut')}
          icon={<ChevronUp />}
          onClick={onPrevResult}
          disabled={results.length === 0}
        />
        <span className="revy-figure text-sm">
          {currentResultIndex + 1} / {results.length}
        </span>
        <TooltipIconButton
          size="icon-sm"
          label={t('documents.nextResultShortcut')}
          icon={<ChevronDown />}
          onClick={onNextResult}
          disabled={results.length === 0}
        />
      </div>
      <Separator />
      <ul className="flex-1 overflow-auto">
        {results.map((result, index) => (
          <li key={index}>
            <button
              type="button"
              onClick={() => onSelectResult(index)}
              className={cn(
                'flex w-full flex-col items-start gap-0.5 px-4 py-2 text-left hover:bg-accent',
                // Same orange as the match rectangle painted on the page, so a
                // row reads as the mark the reader sees in the document.
                index === currentResultIndex && 'bg-[rgba(255,165,0,0.2)] hover:bg-[rgba(255,165,0,0.28)]',
              )}
            >
              <span
                className={cn(
                  'revy-cjk text-sm leading-[1.5]',
                  index === currentResultIndex && 'font-bold',
                )}
              >
                {t('pagination.page')} {result.page}
              </span>
              <span className="revy-cjk w-full truncate text-xs leading-[1.5] text-muted-foreground">
                {result.text}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </aside>
  );
};

export default PdfResultsSidebar;
